/**
 * A contact lead's /pricing estimate, read from `meta.estimate`. The backend
 * writes it (internal/domain/pricing): either a priced estimate recomputed on the
 * published card, or `{input, error}` when it could not be priced. The browser's
 * own figure never reaches the lead, so what renders here is what the card said.
 */

export interface EstimateLine {
  key: string;
  low: number;
  high: number | null;
}

export interface EstimateInput {
  package: string;
  pages: number;
  bilingual: boolean;
  integrations: number;
  mobileApp: boolean;
  inheritedCode: boolean;
  replacingLive: boolean;
  carePlan: string;
}

export type LeadEstimate =
  | {
      kind: 'priced';
      name: { en: string; ar: string };
      low: number;
      high: number | null;
      custom: boolean;
      lines: EstimateLine[];
      care: { name: { en: string; ar: string }; price: number } | null;
      input: EstimateInput;
    }
  | { kind: 'unpriced'; reason: 'unknown_package' | 'unavailable'; input: EstimateInput };

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const text = (v: unknown): string => (typeof v === 'string' ? v : '');

function readInput(v: unknown): EstimateInput {
  const o = obj(v) ?? {};
  return {
    package: text(o.package),
    pages: num(o.pages) ?? 0,
    bilingual: o.bilingual === true,
    integrations: num(o.integrations) ?? 0,
    mobileApp: o.mobile_app === true,
    inheritedCode: o.inherited_code === true,
    replacingLive: o.replacing_live === true,
    carePlan: text(o.care_plan),
  };
}

/** The lead's estimate, or null when the lead did not come from /pricing. */
export function readEstimate(meta: unknown): LeadEstimate | null {
  const e = obj(obj(meta)?.estimate);
  if (!e) return null;
  const input = readInput(e.input);
  if (e.error === 'unknown_package' || e.error === 'unavailable') {
    return { kind: 'unpriced', reason: e.error, input };
  }
  const low = num(e.low);
  if (low === null) return null;
  const name = obj(e.name) ?? {};
  const lines = Array.isArray(e.lines)
    ? e.lines.flatMap((l): EstimateLine[] => {
        const o = obj(l);
        const lo = num(o?.low);
        return o && lo !== null ? [{ key: text(o.key), low: lo, high: num(o.high) }] : [];
      })
    : [];
  const care = obj(e.care);
  const carePrice = num(care?.price);
  const careName = obj(care?.name) ?? {};
  return {
    kind: 'priced',
    name: { en: text(name.en), ar: text(name.ar) },
    low,
    high: num(e.high),
    custom: e.custom === true,
    lines,
    care:
      care && carePrice !== null
        ? { name: { en: text(careName.en), ar: text(careName.ar) }, price: carePrice }
        : null,
    input,
  };
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** "$2,500–$4,500", "$700" for a fixed amount, or "from $12,000" (caller localizes "from"). */
export function formatRange(low: number, high: number | null, from: string): string {
  if (high === null) return `${from} ${usd.format(low)}`;
  if (high === low) return usd.format(low);
  return `${usd.format(low)}–${usd.format(high)}`;
}
